import { authApi } from '@shared/api-client';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useFormError } from '@/hooks/use-form-errors';
import { GoogleSignIn } from '@/components/google-signin';

/** Mirrors the Cognito user pool policy so the failure surfaces before the call. */
const validatePassword = (password: string): string | null => {
  if (password.length < 12) {
    return 'Use at least 12 characters';
  }
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) {
    return 'Include both an uppercase and a lowercase letter';
  }
  if (!/[0-9]/.test(password)) {
    return 'Include at least one number';
  }

  return null;
};

export const SignUpPage = () => {
  const navigate = useNavigate();
  const { error, captureError, clearError } = useFormError();
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();

    const form = new FormData(event.currentTarget);
    const email = String(form.get('email'));
    const password = String(form.get('password'));

    const localPasswordError = validatePassword(password);
    setPasswordError(localPasswordError);

    if (localPasswordError) {
      return;
    }

    setIsSubmitting(true);

    try {
      await authApi.signUp({
        email,
        password,
        name: String(form.get('name') || '') || undefined,
      });

      // Nothing exists in the database yet - the post-confirmation trigger
      // creates the user, organization and first project.
      navigate(`/confirm?email=${encodeURIComponent(email)}`, {
        replace: true,
      });
    } catch (err) {
      captureError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Create your account</CardTitle>
      </CardHeader>

      <CardContent>
        <GoogleSignIn disabled={isSubmitting} />
        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? <Alert tone="error">{error}</Alert> : null}

          <Field label="Name" htmlFor="name">
            <Input name="name" autoComplete="name" placeholder="Alex Chen" />
          </Field>

          <Field
            label="Work email"
            htmlFor="email"
            hint="We send a confirmation link here."
          >
            <Input
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@company.com"
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            error={passwordError}
            hint="At least 12 characters, with upper and lower case and a number."
          >
            <Input
              name="password"
              type="password"
              autoComplete="new-password"
              required
              onChange={() => setPasswordError(null)}
            />
          </Field>

          <Button type="submit" className="w-full" loading={isSubmitting}>
            Create account
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-ink-muted">
          Already have an account?{' '}
          <Link
            to="/signin"
            className="font-medium text-accent underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
